import React, { useState } from 'react';
import useForm from '../hooks/useForm';
import { useChangePassword } from '../hooks/useChangePassword';
import { Eye, EyeOff } from "lucide-react";

function ChangePassword() {
  const { changePassword } = useChangePassword();
  const [formStatus, setFormStatus] = useState(null);

  // Visibility states
  const [currentPasswordVisible, setCurrentPasswordVisible] = useState(false);
  const [newPasswordsVisible, setNewPasswordsVisible] = useState(false);

  const validate = (values) => {
    const errors = {};
    if (!values.current_password) {
      errors.current_password = 'Current Password is required';
    }
    if (!values.new_password) {
      errors.new_password = 'New password is required';
    }
    if (!values.confirm_password) {
      errors.confirm_password = 'Confirm password is required';
    } else if (values.new_password !== values.confirm_password) {
      errors.confirm_password = 'Confirm password does not match new password';
    }
    return errors;
  };

  const onSubmit = async (values) => {
    setFormStatus(null);
    try {
      await changePassword({
        current_password: values.current_password,
        new_password: values.new_password,
        confirm_password: values.confirm_password,
      });
      setFormStatus({ success: 'Password changed successfully' });
      resetForm();
    } catch (error) {
      setFormStatus({ error: error.message || 'Failed to change password' });
    }
  };

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    resetForm,
    
  } = useForm(
    {
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
    validate,
    onSubmit
  );

  return (
    <div className="max-w-md mx-auto p-4">
      <h2 className="text-xl font-semibold mb-4">Change Password</h2>
      <form onSubmit={handleSubmit} noValidate>
        <div className="mb-4 relative">
          <label htmlFor="current_password" className="block mb-1 font-medium">
            Current Password
          </label>
          <input
            type={currentPasswordVisible ? 'text' : 'password'}
            id="current_password"
            name="current_password"
            value={values.current_password}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full border rounded px-3 py-2 ${
              touched.current_password && errors.current_password ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          <button
            type="button"
            onClick={() => setCurrentPasswordVisible(!currentPasswordVisible)}
            className="absolute right-3 top-12 transform -translate-y-1/2 scale-125 text-gray-600"
            tabIndex={-1}
            aria-label={currentPasswordVisible ? 'Hide password' : 'Show password'}
          >
            {currentPasswordVisible ? <Eye className='h-4 w-4'/> : <EyeOff className='h-4 w-4'/>}
          </button>
          {touched.current_password && errors.current_password && (
            <p className="text-red-500 text-sm mt-1">{errors.current_password}</p>
          )}
        </div>

        <div className="mb-4 relative">
          <label htmlFor="new_password" className="block mb-1 font-medium">
            New Password
          </label>
          <input
            type={newPasswordsVisible ? 'text' : 'password'}
            id="new_password"
            name="new_password"
            value={values.new_password}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full border rounded px-3 py-2 ${
              touched.new_password && errors.new_password ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          <button
            type="button"
            onClick={() => setNewPasswordsVisible(!newPasswordsVisible)}
            className="absolute right-3 top-12 transform -translate-y-1/2 scale-125 text-gray-600"
            tabIndex={-1}
            aria-label={newPasswordsVisible ? 'Hide password' : 'Show password'}
          >
            {newPasswordsVisible ? <Eye className='h-4 w-4'/> : <EyeOff className='h-4 w-4'/>}
          </button>
          {touched.new_password && errors.new_password && (
            <p className="text-red-500 text-sm mt-1">{errors.new_password}</p>
          )}
        </div>

        <div className="mb-4 relative">
          <label htmlFor="confirm_password" className="block mb-1 font-medium">
            Confirm Password
          </label>
          <input
            type={newPasswordsVisible ? 'text' : 'password'}
            id="confirm_password"
            name="confirm_password"
            value={values.confirm_password}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full border rounded px-3 py-2 ${
              touched.confirm_password && errors.confirm_password ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          <button
            type="button"
            onClick={() => setNewPasswordsVisible(!newPasswordsVisible)}
            className="absolute right-3 top-12 transform -translate-y-1/2 scale-125 text-gray-600"
            tabIndex={-1}
            aria-label={newPasswordsVisible ? 'Hide password' : 'Show password'}
          >
            {newPasswordsVisible ? <Eye className='h-4 w-4'/> : <EyeOff className='h-4 w-4'/>}
          </button>
          {touched.confirm_password && errors.confirm_password && (
            <p className="text-red-500 text-sm mt-1">{errors.confirm_password}</p>
          )}
        </div>

        {formStatus && formStatus.error && (
          <p className="text-red-600 mb-4">{formStatus.error}</p>
        )}
        {formStatus && formStatus.success && (
          <p className="text-green-600 mb-4">{formStatus.success}</p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {isSubmitting ? 'Changing...' : 'Change Password'}
        </button>
      </form>
    </div>
  );
}

export default ChangePassword;
